// Pengecekan Stok Mockup (Gudang Rido) — baca saja: HANYA hasil AI baca mockup tersimpan, per varian stok, vs stok tersedia.
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { PackageSearch, RefreshCw } from "lucide-react";
import { WhNeedsSummaryBar } from "./needs_board";
import { useAutoRefresh } from "./use_auto_refresh";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sinyal "stok/kebutuhan berubah" (perangkat yang sama): transaksi stok / ACC yang BERHASIL (2xx). Lintas perangkat → interval 60 dtk.
export const STOCK_EVENT = "tam-stock-changed";
export const STOCK_WRITE_RE = /\/api\/(stock\/(in|out|opening-balance|import|reconcile\/apply|transactions\/[^/?]+\/reverse|opname\/[^/?]+\/approve-line)|shopee\/[^/?]+\/(acc-goods|needs\/rebuild)|orders\/[^?]+\/acc-ready)(\?|$)/;
if (typeof window !== "undefined" && !window.__tamStockSignal && axios.interceptors?.response?.use) {
  window.__tamStockSignal = true;
  axios.interceptors.response.use((r) => {
    const m = String(r?.config?.method || "").toLowerCase();
    if (m !== "get" && STOCK_WRITE_RE.test(String(r?.config?.url || ""))) window.dispatchEvent(new CustomEvent(STOCK_EVENT));
    return r;
  });
}
const n = (v) => Number(v || 0).toLocaleString("id-ID", { maximumFractionDigits: 2 });
const lengan = (v) => (!v ? "" : /^lengan/i.test(v) ? v : `Lengan ${String(v).toLowerCase()}`);
const variasi = (i) => [i.color, lengan(i.sleeve), i.size].filter(Boolean).join(" · ");

function Item({ i }) {
  const habis = i.state === "habis";
  return <div className="scb-item" data-testid={`scb-item-${i.variant_id}`}>
    <div className="scb-item-top"><div><b>{i.product}</b><span className="scb-var">{variasi(i) || "—"}</span></div>
      <span className={`scb-badge is-${i.state}`}>{habis ? "Stok habis" : i.state === "kurang" ? `Kurang ${n(i.shortage)}` : "Cukup"}</span></div>
    <div className="scb-nums">
      <span>Dibutuhkan<b>{n(i.need)} pcs</b></span><span>Stok tersedia<b>{n(i.stock)} pcs</b></span>
      <span>Kekurangan<b className={i.shortage > 0 ? "is-short" : ""}>{n(i.shortage)} pcs</b></span></div>
    <div className="scb-orders" data-testid={`scb-orders-${i.variant_id}`}>Pesanan: {i.orders.map(o => `#${o.order_number} (${n(o.qty)})`).join(", ")}</div>
  </div>;
}

function Section({ id, title, count, children }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return <div className="scb-sec">
    <button type="button" className="scb-sec-btn" aria-expanded={open} onClick={() => setOpen(v => !v)} data-testid={`scb-sec-${id}`}>
      <span>{title} ({count})</span><span className="nbx-more">{open ? "Tutup ▴" : "Lihat ▾"}</span></button>
    {open && <div className="scb-sec-body" data-testid={`scb-sec-body-${id}`}>{children}</div>}
  </div>;
}

export function StockCheckBoard() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => axios.get(`${API}/shopee/warehouse/stock-check`, { withCredentials: true, timeout: 30000 })
    .then(r => { setD(r.data); setErr(""); })
    .catch(e => { setErr(e?.response?.data?.detail || "Gagal memuat Pengecekan Stok Mockup"); throw e; }), []);
  const { refresh, isRefreshing } = useAutoRefresh({ intervalMs: 60000, onRefresh: load, pauseWhenDirty: false });
  const last = useRef(0);
  const soon = useRef(null);
  // Muat pertama, saat tab aktif/halaman dibuka lagi (maks 1x/10 dtk), dan setelah sinyal transaksi (digabung 1,5 dtk).
  useEffect(() => {
    const go = () => { last.current = Date.now(); refresh(); };
    const back = () => { if (document.visibilityState === "visible" && Date.now() - last.current > 10000) go(); };
    const sig = () => { clearTimeout(soon.current); soon.current = setTimeout(go, 1500); };
    go();
    document.addEventListener("visibilitychange", back);
    window.addEventListener("focus", back);
    window.addEventListener(STOCK_EVENT, sig);
    return () => { clearTimeout(soon.current); document.removeEventListener("visibilitychange", back); window.removeEventListener("focus", back); window.removeEventListener(STOCK_EVENT, sig); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const head = <div className="scb-head"><PackageSearch size={18}/><b>Pengecekan Stok Mockup</b>
    <button type="button" className="link-btn" onClick={refresh} disabled={isRefreshing} aria-label="Muat ulang" data-testid="scb-refresh"><RefreshCw size={14} className={isRefreshing ? "spin" : ""}/></button></div>;
  if (!d) return <div className="nbx scb" data-testid="stock-check-board">{head}
    {err ? <p className="nbx-err small" data-testid="scb-error">{String(err)} <button type="button" className="link-btn small" onClick={refresh} data-testid="scb-retry">Coba lagi</button></p>
      : <p className="muted small" data-testid="scb-loading">Memuat…</p>}</div>;
  const items = d.items || [];
  const bad = items.filter(i => i.state !== "cukup"), ok = items.filter(i => i.state === "cukup");
  const pend = d.pending_orders || [], held = d.held || [];
  const full = pend.filter(p => !p.partial).length;
  return <div className="nbx scb" data-testid="stock-check-board">
    {head}
    {err && <p className="nbx-err small" data-testid="scb-stale">Gagal memperbarui — data terakhir ditampilkan.</p>}
    <p className="muted small scb-src" data-testid="scb-source">Sumber: hasil AI baca mockup final, dicocokkan dengan stok TAMFARIS.</p>
    <div className="scb-stats">{[["kurang", "Stok kurang"], ["habis", "Stok habis"], ["cukup", "Stok cukup"]].map(([k, l]) =>
      <div key={k} className={`scb-stat is-${k}`} data-testid={`scb-count-${k}`}><b>{d.counts?.[k] ?? 0}</b><span>{l}</span></div>)}</div>
    <p className={`scb-cover small${pend.length ? " is-warn" : ""}`} data-testid="scb-coverage">
      {pend.length ? `${d.complete_orders} dari ${d.active_orders} pesanan aktif sudah terbaca AI & cocok stok · ${pend.length} belum bisa dicek${pend.length > full ? ` (${pend.length - full} sebagian)` : ""} — pengecekan stok belum lengkap.`
        : `Semua ${d.active_orders} pesanan aktif sudah terbaca AI & cocok stok.`}</p>
    <div className="scb-title">Barang yang perlu diperhatikan</div>
    {bad.length ? bad.map(i => <Item key={i.variant_id} i={i}/>)
      : <p className="muted small" data-testid="scb-none">Tidak ada stok kurang atau habis dari pesanan yang sudah terbaca AI.</p>}
    <Section id="pending" title="Belum Bisa Dicek" count={pend.length}>
      {pend.map(p => <div key={p.job_id} className="scb-row" data-testid={`scb-pending-${p.order_number}`}>
        <b>#{p.order_number}</b>{p.buyer && <span className="muted"> · {p.buyer}</span>}{p.partial && <span className="scb-badge is-kurang">Sebagian dihitung</span>}
        <div className="muted small">{p.reasons.join(" · ")}</div></div>)}
    </Section>
    <Section id="held" title="Ditahan (tidak dihitung)" count={held.length}>
      {held.map(h => <div key={h.job_id} className="scb-row" data-testid={`scb-held-${h.order_number}`}><b>#{h.order_number}</b></div>)}
    </Section>
    <Section id="ok" title="Stok Cukup" count={ok.length}>
      {ok.map(i => <div key={i.variant_id} className="scb-row" data-testid={`scb-ok-${i.variant_id}`}>
        <b>{i.product}</b> <span className="muted">{variasi(i)}</span><div className="muted small">Butuh {n(i.need)} · Stok {n(i.stock)} pcs</div></div>)}
    </Section>
    <WhNeedsSummaryBar compact/>
  </div>;
}
